
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Camera, Video } from 'lucide-react';
import { CreateSnapSheet } from './snap/CreateSnapSheet';
import { CreateDroolSheet } from './drool/CreateDroolSheet';

interface CreateContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: 'snap' | 'drool';
}

type ContentType = 'choice' | 'snap' | 'drool';

export const CreateContentModal: React.FC<CreateContentModalProps> = ({
  isOpen,
  onClose,
  initialType,
}) => {
  const [currentView, setCurrentView] = useState<ContentType>(initialType ?? 'choice');

  const handleClose = () => {
    setCurrentView(initialType ?? 'choice');
    onClose();
  };

  const handleSuccess = () => {
    setCurrentView(initialType ?? 'choice');
    onClose();
  };

  const snapIsOpen = isOpen && (currentView === 'snap' || initialType === 'snap');
  const droolIsOpen = isOpen && (currentView === 'drool' || initialType === 'drool');

  // Both snap and drool get their own bottom sheets
  if (snapIsOpen) {
    return (
      <CreateSnapSheet
        isOpen={snapIsOpen}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
    );
  }

  if (droolIsOpen) {
    return (
      <CreateDroolSheet
        isOpen={droolIsOpen}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
    );
  }

  return (
    <>
      {/* Sheets (opened from choice screen) */}
      <CreateSnapSheet
        isOpen={currentView === 'snap' && isOpen}
        onClose={() => setCurrentView('choice')}
        onSuccess={handleSuccess}
      />
      <CreateDroolSheet
        isOpen={currentView === 'drool' && isOpen}
        onClose={() => setCurrentView('choice')}
        onSuccess={handleSuccess}
      />

      {/* Dialog for choice screen only */}
      <Dialog open={isOpen && currentView === 'choice'} onOpenChange={handleClose}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle>Create Content</DialogTitle>
            <DialogDescription>What would you like to share today?</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                What would you like to share?
              </h3>
              <p className="text-muted-foreground text-sm">
                Choose between sharing a photo or a video
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => setCurrentView('snap')}
                className="group relative overflow-hidden rounded-2xl bg-neutral-900/80 border border-white/10 hover:border-[#cfef00] hover:bg-neutral-800/80 p-6 text-white transition-all duration-300"
              >
                <div className="relative z-10 flex items-center space-x-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <Camera className="h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-xl font-bold">Create a Snap</h4>
                    <p className="text-muted-foreground text-sm">Post a photo and tag the dish you ate</p>
                  </div>
                </div>
                <div className="absolute -right-2 -top-2 h-24 w-24 rounded-full bg-white/10 backdrop-blur-sm"></div>
              </button>

              <button
                onClick={() => setCurrentView('drool')}
                className="group relative overflow-hidden rounded-2xl bg-neutral-900/80 border border-white/10 hover:border-[#cfef00] hover:bg-neutral-800/80 p-6 text-white transition-all duration-300"
              >
                <div className="relative z-10 flex items-center space-x-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <Video className="h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-xl font-bold">Create a Drool</h4>
                    <p className="text-muted-foreground text-sm">Post a short video of your food moment</p>
                  </div>
                </div>
                <div className="absolute -right-2 -top-2 h-24 w-24 rounded-full bg-white/10 backdrop-blur-sm"></div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
